import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DateCell, ExtendedNumberCell, RadioCell } from '@work4all/components';
import { TaskPicker } from '@work4all/components/lib/components/entity-picker/task-picker/TaskPicker';
import { TicketPicker } from '@work4all/components/lib/components/entity-picker/ticket-picker/TicketPicker';
import { EditableCell } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/editable-cell/EditableCell';
import { EditModeCell } from '@work4all/components/lib/dataDisplay/basic-table/plugins/useEditMode';

import { CheckListPosition } from '@work4all/models/lib/Classes/CheckListPosition.entity';
import { Task } from '@work4all/models/lib/Classes/Task.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { Filters } from '@work4all/models/lib/DataProvider';
import { ChecklistePositionPrioritaet } from '@work4all/models/lib/Enums/ChecklistePositionPrioritaet.enum';
import { ChecklistPositionKind } from '@work4all/models/lib/Enums/ChecklistPositionKind.enum';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { TaskStatus } from '@work4all/models/lib/Enums/TaskStatus.enum';
import { TicketStatus } from '@work4all/models/lib/Enums/TicketStatus.enum';

import { useDeepMemo } from '@work4all/utils/lib/hooks/use-deep-memo';
import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { useFormContextPlus } from '../../../../../../../../form-plus/use-form-context-plus';
import { CheckListPositionPriorityCell } from '../../../../../../components/table-cells/CheckListPositionPriorityCell';
import { EntitySuggestionPickerCell } from '../../../../../../components/table-cells/EntitySuggestionPickerCell';
import { ProjectProcessPickerCell } from '../../../../../../components/table-cells/ProjectProcessPickerCell';
import { SupplierPickerCell } from '../../../../../../components/table-cells/SupplierPickerCell';
import { UserPickerCell } from '../../../../../../components/table-cells/UserPickerCell';
import {
  EditTableColumns,
  OnEditPosition,
} from '../../../../../../erp/components/tab-panels/positions/components/edit-table/types';
import { CheckListMaskFormValue } from '../../../../types';
import { CheckListPositionsPickerCell } from '../components/CheckListPositionsPickerCell';

type Props = {
  onEdit: (result: OnEditPosition<CheckListPosition<EMode.entity>>) => void;
  columnSettings: {
    visibility: string[];
    order: string[];
    width: Record<string, number>;
  };
};

type Column = EditTableColumns<PathsOf<CheckListPosition, 2>>;
type Return = Column[];

const KINDS_WITH_DISABLED_COLUMN = [
  ChecklistPositionKind.HEADLINE,
  ChecklistPositionKind.EMPTY_ROW,
];

const generateTitle = (position: CheckListPosition) => {
  const dateString = DateTime.fromISO(position.date).toFormat('dd.MM.yyyy');
  return `Checkliste ${dateString}, Pos. ${position.positionNumber}: ${
    position.name || ''
  }`;
};

export const useCheckListPositionsColumns = ({
  onEdit,
  columnSettings,
}: Props): Return => {
  const { t } = useTranslation();
  const { getValues } = useFormContextPlus<CheckListMaskFormValue>();

  const columns: Column[] = useMemo(() => {
    return [
      {
        id: 'priority',
        Header: t('FIELDS.priority_short'),
        type: 'picker',
        accessor: 'priority',
        sticky: 'left',
        Cell: (cell) => {
          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          return (
            <CheckListPositionPriorityCell
              value={cell.value}
              onChange={(value) => {
                onEdit({
                  forceUpdate: true,
                  position: {
                    ...cell.row.original,
                    priority: value || ChecklistePositionPrioritaet.NORMAL,
                  },
                });
              }}
            />
          );
        },
        width: 50,
        style: { padding: undefined, display: 'block' },
      },
      {
        id: 'isChecked',
        Header: t('CHECK_LIST_POSITIONS_TABLE.DONE'),
        accessor: 'isChecked',
        sticky: 'left',
        width: 50,
        Cell: (cell) => {
          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          return (
            <RadioCell
              value={cell.value}
              onChange={(isChecked) => {
                onEdit({
                  forceUpdate: true,
                  position: {
                    ...cell.row.original,
                    doneDate: isChecked ? new Date().toISOString() : null,
                    isChecked,
                  },
                });
              }}
            />
          );
        },
      },
      {
        id: 'positionNumber',
        Header: t('COMMON.NUMBER_SHORT'),
        type: 'text',
        accessor: 'positionNumber',
        sticky: 'left',
        width: 50,
        Cell: (cell) => {
          const isEditMode = cell.isEditMode;

          if (cell.row.original.kind === ChecklistPositionKind.EMPTY_ROW) {
            return '';
          }

          if (isEditMode) {
            return (
              <EditableCell
                {...(cell as object)}
                isEditable
                isEditMode
                cell={cell as unknown as EditModeCell & ExtendedNumberCell}
                onChange={(positionNumber: string) => {
                  onEdit({
                    forceUpdate: true,
                    position: {
                      ...cell.row.original,
                      positionNumber,
                    },
                  });
                }}
              />
            );
          }

          return cell.value ?? '';
        },
      },
      {
        id: 'task',
        Header: t('COMMON.TASK'),
        accessor: 'task',
        width: 200,
        Cell: (cell) => {
          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          const businessPartnerId = getValues('businessPartnerId');
          const prefilter: Filters = [
            {
              status: { $eq: TaskStatus.OFFEN },
            },
          ];

          if (businessPartnerId) {
            prefilter.push({
              'businessPartner.id': {
                $eq: businessPartnerId,
              },
            });
          }

          const position: CheckListPosition = cell.row.original;

          const presetFields: Task = {
            title: generateTitle(position),
            note:
              (position.name || '') +
              (position.note ? '\n\n------\n\n' + position.note : ''),
            date: position.date,
            endDate: position.endDate,
            userId: position.plannedByUserId,
            user: position.userPlanned,
            project: getValues('project'),
            projectId: getValues('projectId'),
            projectProcess: getValues('projectProcess'),
            projectProcessId: getValues('projectProcessId'),
            businessPartner: getValues('businessPartner'),
            businessPartnerId: getValues('businessPartnerId'),
            contact: getValues('contact'),
            contactId: getValues('contactId'),
          };

          return (
            <CheckListPositionsPickerCell
              presetFields={presetFields}
              value={cell.value?.title}
              picker={
                <TaskPicker
                  value={cell.value}
                  onChange={(task) => {
                    onEdit({
                      forceUpdate: true,
                      position: {
                        ...cell.row.original,
                        task: task ?? undefined,
                      },
                    });
                  }}
                  multiple={false}
                  prefilter={prefilter}
                  layout="advanced"
                />
              }
              entity={Entities.task}
              disabled={Boolean(cell.row.original.ticket)}
              onChange={(task) => {
                onEdit({
                  forceUpdate: true,
                  position: { ...cell.row.original, task },
                });
              }}
            />
          );
        },
      },
      {
        id: 'ticket',
        Header: t('COMMON.TICKET'),
        accessor: 'ticket',
        width: 200,
        Cell: (cell) => {
          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          const businessPartnerId = getValues('businessPartnerId');
          const prefilter: Filters = [
            {
              status1: { $eq: TicketStatus.OFFEN },
            },
          ];

          if (businessPartnerId) {
            prefilter.push({
              'businessPartner.id': {
                $eq: businessPartnerId,
              },
            });
          }

          const position: CheckListPosition = cell.row.original;

          const presetFields: Ticket = {
            title: generateTitle(position),
            problemDescription:
              (position.name || '') +
              (position.note
                ? '<br/><br/>------<br/><br/>' + position.note
                : ''),
            followUpDate: position.date,
            editor1: position.userPlanned,
            editor1Id: position.userPlanned?.id,
            project: getValues('project'),
            projectId: getValues('projectId'),
            projectProcess: getValues('projectProcess'),
            projectProcessId: getValues('projectProcessId'),
            businessPartner: getValues('businessPartner'),
            businessPartnerId: getValues('businessPartnerId'),
            contact: getValues('contact'),
            contactId: getValues('contactId'),
          };

          return (
            <CheckListPositionsPickerCell
              value={cell.value?.number}
              presetFields={presetFields}
              picker={
                <TicketPicker
                  value={cell.value}
                  onChange={(ticket) => {
                    onEdit({
                      forceUpdate: true,
                      position: {
                        ...cell.row.original,
                        ticket: ticket ?? undefined,
                      },
                    });
                  }}
                  multiple={false}
                  prefilter={prefilter}
                  layout="advanced"
                />
              }
              entity={Entities.ticket}
              disabled={Boolean(cell.row.original.task)}
              onChange={(ticket) => {
                onEdit({
                  forceUpdate: true,
                  position: { ...cell.row.original, ticket },
                });
              }}
            />
          );
        },
      },
      {
        id: 'name',
        Header: t('COMMON.CONTENT'),
        type: 'text',
        accessor: 'name',
        width: 200,
      },
      {
        id: 'note',
        Header: t('CHECK_LIST_POSITIONS_TABLE.NOTE'),
        type: 'text',
        accessor: 'note',
        width: 200,
      },
      {
        id: 'plannedBy',
        Header: t('CHECK_LIST_POSITIONS_TABLE.USER'),
        accessor: 'plannedBy',
        width: 200,
        render: (cell, options: { isEditMode?: boolean } = {}) => {
          const isEditMode = options?.isEditMode || false;

          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          return (
            <EntitySuggestionPickerCell<User>
              entity={Entities.user}
              field="displayName"
              value={cell.value}
              onChange={(result) => {
                onEdit({
                  forceUpdate: true,
                  position: {
                    ...cell.row.original,
                    plannedBy: result?.obj?.displayName ?? undefined,
                    plannedByUserId: result?.obj?.id ?? 0,
                    userPlanned: result?.obj ?? undefined,
                  },
                });
              }}
              isEditMode={isEditMode}
              startWithWildcard
              data={{
                id: null,
                displayName: null,
              }}
            />
          );
        },
      },
      {
        id: 'date',
        Header: t('COMMON.BEGINNING'),
        type: 'date',
        accessor: 'date',
        width: 150,
        Cell: (e) => (
          <DateCell
            {...e}
            column={{
              cellParams: {
                format: 'DATE_SHORT',
              },
            }}
          />
        ),
      },
      {
        id: 'endDate',
        Header: t('COMMON.ENDDATE'),
        type: 'date',
        accessor: 'endDate',
        width: 150,
        Cell: (e) => (
          <DateCell
            {...e}
            column={{
              cellParams: {
                format: 'DATE_SHORT',
              },
            }}
          />
        ),
      },
      {
        id: 'userDone',
        Header: t('CHECK_LIST_POSITIONS_TABLE.DONE_FROM'),
        type: 'text',
        accessor: 'userDone',
        width: 200,
        render: (cell, options: { isEditMode?: boolean } = {}) => {
          const isEditMode = options?.isEditMode || false;

          if (isEditMode) {
            return (
              <UserPickerCell
                hideIcon
                value={cell.value}
                onChange={(user) => {
                  onEdit({
                    forceUpdate: true,
                    position: {
                      ...cell.row.original,
                      userDone: user ?? undefined,
                      doneByUserId: user?.id ?? 0,
                    },
                  });
                }}
              />
            );
          }

          return cell.value?.displayName ?? '';
        },
      },
      {
        id: 'category1',
        Header: t('COMMON.CATEGORY1'),
        type: 'text',
        accessor: 'category1',
        width: 150,
        render: (cell, options: { isEditMode?: boolean } = {}) => {
          const isEditMode = options?.isEditMode || false;

          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          return (
            <EntitySuggestionPickerCell<CheckListPosition>
              entity={Entities.checkListPosition}
              field="category1"
              value={cell.value}
              onChange={(result) => {
                onEdit({
                  forceUpdate: true,
                  position: {
                    ...cell.row.original,
                    category1: result.value,
                  },
                });
              }}
              isEditMode={isEditMode}
            />
          );
        },
      },
      {
        id: 'category2',
        Header: t('COMMON.CATEGORY2'),
        type: 'text',
        accessor: 'category2',
        width: 150,
        render: (cell, options: { isEditMode?: boolean } = {}) => {
          const isEditMode = options?.isEditMode || false;

          if (KINDS_WITH_DISABLED_COLUMN.includes(cell.row.original.kind)) {
            return null;
          }

          return (
            <EntitySuggestionPickerCell<CheckListPosition>
              entity={Entities.checkListPosition}
              field="category2"
              value={cell.value}
              onChange={(result) => {
                onEdit({
                  forceUpdate: true,
                  position: {
                    ...cell.row.original,
                    category2: result.value,
                  },
                });
              }}
              isEditMode={isEditMode}
            />
          );
        },
      },
      {
        id: 'supplier',
        Header: t('COMMON.SUPPLIER'),
        type: 'text',
        accessor: 'supplier',
        width: 200,
        render: (cell, options: { isEditMode?: boolean } = {}) => {
          const isEditMode = options?.isEditMode || false;

          if (isEditMode) {
            return (
              <SupplierPickerCell
                value={cell.value}
                onChange={(supplier) => {
                  onEdit({
                    forceUpdate: true,
                    position: {
                      ...cell.row.original,
                      supplier: supplier ?? undefined,
                      supplierId: supplier?.id ?? 0,
                    },
                  });
                }}
              />
            );
          }

          return cell.value?.name ?? '';
        },
      },

      {
        id: 'projectProcess',
        Header: t('COMMON.PROJECTPROCESS'),
        type: 'text',
        accessor: 'projectProcess',
        width: 200,
        render: (cell, options: { isEditMode?: boolean } = {}) => {
          const isEditMode = options?.isEditMode || false;

          if (cell.row.original.kind !== ChecklistPositionKind.DEFAULT)
            return '';
          const projectId = getValues('projectId');
          if (isEditMode && projectId) {
            return (
              <ProjectProcessPickerCell
                value={cell.value}
                projectId={projectId}
                onChange={(projectProcess) => {
                  onEdit({
                    forceUpdate: true,
                    position: {
                      ...cell.row.original,
                      projectProcess: projectProcess ?? undefined,
                      projectProcessId: projectProcess?.id ?? 0,
                    },
                  });
                }}
              />
            );
          }

          return cell.value?.process ?? '';
        },
      },
    ];
  }, [getValues, onEdit, t]);

  const columnsWithSavedWidth: Column[] = useDeepMemo(() => {
    return columns.map((column) => {
      return {
        ...column,
        width: columnSettings.width[column.id] ?? column.width,
      };
    });
  }, [columnSettings.width, columns]);

  return columnsWithSavedWidth;
};
